import { isEmpty } from "lodash";
import { v4 as uuidv4 } from "uuid";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, Input } from "@mui/material";
import { IMaskInput } from "react-imask";
import { t } from "i18next";
import moment from "moment";
import clsx from "clsx";

import { TourSchedularForm, Icon, ButtonComp, CustomFormQuestion, CustomTooltip } from "components";
import {
  getButtonBgColor,
  getIsMaximized,
  selectIsDisabled,
  selectLabelItems,
  selectModuleName,
  setCheckBox,
  setLabelItems,
  setVisibleCalendar,
  setSelectedDate,
  getContactTransactionalMsg,
  getHyformsTransactionalMsg,
  getContactPromotionMsg,
  getHyformsPromotionMsg,
  getTourTransactionalMsg,
  getHyTourTransactionalMsg,
  getTourPromotionMsg,
  getHyToursPromotionMsg,
  setShowTourSMSOpt,
  setShowContactSMSOpt,
  getEnabledScheduleTour,
  getScheduleDateAvail,
  selectSelectedOptions,
  getSelfTourLink,
  selectAvailableSlots,
  selectSlotsLoading,
  selectPropertyId,
  getFormDisclaimer,
  getFormDisclaimerData,
} from "slices";
import { convertToCamelCase, validator, textConstants } from "utils";

const PhoneMaskCustom = React.forwardRef(function TextMaskCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask="(#00) 000-0000"
      definitions={{ "#": /[1-9]/ }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      onChange={() => {}}
      overwrite
    />
  );
});

const MultiInputView = ({ replies, onClick, onCancel = () => {}, messagesContainerRef = null }) => {
  const dispatch = useDispatch();
  const isMaximized = useSelector(getIsMaximized);
  const backgroundColor = useSelector(getButtonBgColor);
  const prevLabel = useSelector(selectLabelItems);
  const module = useSelector(selectModuleName);
  const optionDisabled = useSelector(selectIsDisabled);
  const disclaimerData = useSelector(getFormDisclaimerData);
  const showContactTransactionMsg = useSelector(getContactTransactionalMsg);
  const showContactPromotionMsg = useSelector(getContactPromotionMsg);
  const contactTransactionMsg = useSelector(getHyformsTransactionalMsg);
  const contactPromotionMsg = useSelector(getHyformsPromotionMsg);
  const showTourTransactionMsg = useSelector(getTourTransactionalMsg);
  const showTourPromotionMsg = useSelector(getTourPromotionMsg);
  const tourTransactionMsg = useSelector(getHyTourTransactionalMsg);
  const tourPromotionMsg = useSelector(getHyToursPromotionMsg);
  const schedularFormEnabled = useSelector(getEnabledScheduleTour);
  const scheduleDateAvail = useSelector(getScheduleDateAvail);
  const self_tour_link = useSelector(getSelfTourLink);
  const { tourType } = useSelector(selectSelectedOptions);
  const slotsLoading = useSelector(selectSlotsLoading);
  const propertyId = useSelector(selectPropertyId);
  const isFormDisclaimerEnabled = useSelector(getFormDisclaimer);

  const isRescheduleForm = !!replies?.isReschedule;

  const localStorageKey = `${propertyId}-booking_details`;
  const storedData = JSON.parse(localStorage.getItem(localStorageKey));

  const { date: availableDateSlots } = useSelector(selectAvailableSlots);
  const labelObj = { ...prevLabel };

  const [errorMessage, setErrorMessage] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [questionDisabled, setQuestionDisabled] = useState(true);

  const getInitialInputValues = () => {
    const base = {
      appointment_type: prevLabel.appointment_type || "",
      date: "",
      time: "",
    };

    if (isRescheduleForm) {
      return {
        ...base,
        first_name: storedData?.first_name || "",
        last_name: storedData?.last_name || "",
        email: storedData?.email || "",
        phone: storedData?.phone || "",
      };
    }

    return base;
  };

  const [inputValues, setInputValues] = useState(getInitialInputValues);

  const [customQuestions, setCustomQuestions] = useState({});
  const [isChecked, setIsChecked] = useState(false);

  const [isCheckedTourTransaction, setIsCheckedTourTransaction] = useState(false);
  const [isCheckedTourPromotion, setIsCheckedTourPromotion] = useState(false);

  const [isCheckedContactTransaction, setIsCheckedContactTransaction] = useState(false);
  const [isCheckedContactPromotion, setIsCheckedContactPromotion] = useState(false);

  const [validating, setValidating] = useState({ email: true });
  const [showShadow, setShowShadow] = useState(false);

  const firstInputRef = useRef(null);

  const isDisabledCheckbox = !validating["phone"];

  const handleTourTransactionChange = (isScheduleTourForm) => {
    isScheduleTourForm ? setIsCheckedTourTransaction(!isCheckedTourTransaction) : setIsCheckedContactTransaction(!isCheckedContactTransaction);
  };

  const handleTourPromotionChange = (isScheduleTourForm) => {
    isScheduleTourForm ? setIsCheckedTourPromotion(!isCheckedTourPromotion) : setIsCheckedContactPromotion(!isCheckedContactPromotion);
  };

  const handleShowAll = useCallback(() => {
    dispatch(setSelectedDate(""));
    dispatch(setVisibleCalendar(true));
  }, [dispatch]);

  useEffect(() => {
    const threshold = {
      maximized: 80,
      contactUs: 80,
      scheduleTour: 189,
    };

    if (!messagesContainerRef?.current || isMaximized) {
      return;
    }

    if (
      (replies.optionsType === "Form4ContactUs" && messagesContainerRef.current.scrollTop < threshold.contactUs) ||
      (replies.optionsType === "Form4ScheduleTour" && messagesContainerRef.current.scrollTop < threshold.scheduleTour)
    ) {
      setShowShadow(true);
    } else {
      setShowShadow(false);
    }
  }, [messagesContainerRef?.current.scrollTop]);

  useEffect(() => {
    if (firstInputRef.current) {
      const inputElement = firstInputRef.current.querySelector("input");

      if (inputElement) {
        setTimeout(() => {
          inputElement.focus();
        }, [300]);
      }
    }
  }, []);

  useEffect(() => {
    if (isDisabledCheckbox) {
      setIsChecked(false);
    }
  }, [isDisabledCheckbox]);

  useEffect(() => {
    validationCheck();
  }, [inputValues, replies.options]);

  useEffect(() => {
    const inputValuesTemp = inputValues;

    replies.options.forEach(({ key }) => {
      // If there is a value in localStorage or redux, set it to inputValues
      if (prevLabel[key]) {
        const isValidate = validator(prevLabel[key], key.toUpperCase());

        setValidating({
          ...validating,
          [key]: isValidate,
        });

        inputValuesTemp[key] = prevLabel[key];
      }
    });

    setInputValues(inputValuesTemp);
  }, [replies.options]);

  const onChange = (e, key, validate) => {
    // Limit input length
    const maxLengths = { first_name: 30, last_name: 30, email: 254 };

    if (maxLengths[key] && e.target.value.length > maxLengths[key]) {
      return;
    }

    if (validate === "TEXT_ONLY") {
      if (validator(e.target.value, validate) || isEmpty(e.target.value)) {
        setInputValues({ ...inputValues, [key]: e.target.value });
      }
    } else {
      if (["EMAIL", "PHONE"].includes(validate)) {
        const isValidate = validator(e.target.value, validate);

        setValidating({
          ...validating,
          [key]: isValidate,
        });
      }

      setInputValues({ ...inputValues, [key]: e.target.value });
    }
  };

  const onClickSend = () => {
    let message = `<div class="hayley-flex hayley-flex-col hayley-items-center hayley-justify-end hayley-overflow-wrap hayley-break-all">`;
    let nextOptionsType = replies.options[0]["nextOptionsType"];

    replies.options.forEach(({ key }) => {
      const value = inputValues[key] || "";

      if (key === "first_name" || key === "last_name") {
        message += `${value} `;
      } else {
        message += `<br />${value}`;
      }

      labelObj[key] = value;
    });

    for (const key in inputValues) {
      labelObj[key] = inputValues[key];
    }

    const isQuestions = Object.keys(customQuestions || {})?.length;

    if (isQuestions) {
      message += `<br />`;

      Object.values(customQuestions)?.forEach(({ question, answer }) => {
        if (answer == "true") {
          message += `<br />${question}<br />Yes`;
        } else if (answer == "false") {
          message += `<br />${question}<br />No`;
        } else if (answer) {
          message += `<br />${question}<br />${answer}`;
        }
      });
    }

    const eventHash = {
      event_name: "h_click",
      event_params: {
        h_page_type: "hayley",
        h_clicked_location: "scheduler_submit",
        h_stream: "hyly",
        h_display_method: "iframe",
      },
    };

    if (module === "schedule_tour") {
      eventHash.event_params.h_tour_type = convertToCamelCase(labelObj.appointment_type);
      eventHash.event_params.h_start_date = labelObj.date;
      eventHash.event_params.h_start_time = moment(labelObj.time).format("HH:mm a");
    }

    if (module === "contact_us") {
      eventHash.event_params.h_clicked_location = "contact_us_submit";
      window.parent.postMessage(JSON.stringify(eventHash), "*");
    }

    replies.title !== "Enter First Name and Last Name" && window.parent.postMessage(JSON.stringify(eventHash), "*");

    dispatch(setLabelItems(labelObj));
    const isChecked = isScheduleTourForm ? isCheckedTourTransaction || isCheckedTourPromotion : isCheckedContactTransaction || isCheckedContactPromotion;
    dispatch(setCheckBox(isChecked));
    dispatch(setShowTourSMSOpt(isCheckedTourTransaction || isCheckedTourPromotion));
    dispatch(setShowContactSMSOpt(isCheckedContactTransaction || isCheckedContactPromotion));

    // nextOptionsType = replies?.isFromFaq ? "PostScheduleTourFromFaq" : nextOptionsType;

    onClick([{ message, speaker: "user", isAlignRight: true, optionsType: replies?.optionsType }], nextOptionsType, false, undefined);
  };

  // Trigger onClick method by clicking on enter key
  const onKeyPress = (e) => {
    if (e.key === "Enter" && !disabled && !optionDisabled) {
      onClickSend();
    }
  };

  const validationCheck = () => {
    let values = {};

    replies.options.forEach(
      ({
        key,
        content: {
          componentOptions: { validate, isOptional },
        },
      }) => {
        if (isEmpty(inputValues[key])) {
          if (isOptional) {
            values[key] = true;
          }
        } else if (validate) {
          values[key] = validator(inputValues[key], validate);
        }
      }
    );

    const checkValues = replies.options.every(({ key }) => values[key]);

    setDisabled(!checkValues);

    if (!checkValues) {
      setErrorMessage(t("enterValidValue"));
    } else {
      setErrorMessage("");
    }
  };

  const isScheduleTourForm = replies?.optionsType === "Form4ScheduleTour";
  // const isContactTourForm = replies?.optionsType === "Form4ContactUs";

  const getDisclaimer = (data) => {
    let disclaimer = { title: "", message: "" };

    try {
      if (typeof data === "string") {
        // Handle Ruby-style hash string by converting => to :
        const cleaned = data.replace(/=>/g, ":");

        disclaimer = JSON.parse(cleaned);
      } else if (typeof data === "object" && data !== null) {
        // Already parsed object
        disclaimer = data;
      }
    } catch (err) {
      console.error("Failed to parse disclaimer:", err);
    }

    return disclaimer;
  };

  return (
    <div className={`${isScheduleTourForm ? "hayley-rounded-[8px] hayley-bg-[#FBFBFB] hayley-px-3 hayley-py-4" : ""}`}>
      <div className={`hayley-flex hayley-w-full hayley-flex-wrap hayley-items-center ${isMaximized ? "!hayley-w-[500px]" : ""}`}>
        {isScheduleTourForm && schedularFormEnabled ? (
          <TourSchedularForm inputValues={inputValues} setInputValues={setInputValues} handleShowAll={handleShowAll} />
        ) : null}

        {isScheduleTourForm && !availableDateSlots.length && !slotsLoading ? (
          <div>
            <p
              className={`hayley-m-0 hayley-mb-2 ${
                isMaximized ? "hayley-mt-8" : "hayley-mt-5"
              }  hayley-text-[#666]" data-testid="scheduleTourForm_slotsNotAvailable hayley-text-sm`}
            >
              Kindly select another type as there are no slots available.
            </p>
            <ButtonComp label={t("cancel")} onClick={onCancel} />
          </div>
        ) : self_tour_link && tourType === "self_guided_tour" ? (
          <div
            className={clsx(
              `${!isScheduleTourForm ? "-hayley-mx-4 hayley-bg-white hayley-px-4" : "-hayley-mx-3 hayley-bg-[#FBFBFB] hayley-px-3 "}`, // Adjust "another-class" as needed
              "hayley-sticky -hayley-bottom-4  hayley-my-[1px] hayley-flex hayley-w-[inherit] hayley-gap-3 hayley-overflow-hidden   hayley-py-3",
              { "hayley-shadow-onesided": showShadow }
            )}
          >
            <ButtonComp label={t("cancel")} onClick={onCancel} />
          </div>
        ) : scheduleDateAvail ? (
          <span className="hayley-text-sm hayley-font-semibold hayley-text-custom-8">{textConstants.datesNotAvail}</span>
        ) : (
          <>
            <div className="hayley-flex hayley-w-full hayley-items-end hayley-space-x-4">
              <div className="hayley-grid hayley-flex-1 hayley-grid-cols-2 hayley-gap-4">
                {replies.options.map(
                  (
                    {
                      content: {
                        componentOptions: { type, placeholder, validate },
                      },
                      key,
                      isRequired,
                    },
                    idx
                  ) => {
                    const isValid = ["email", "phone_number", "phone"].includes(key) && inputValues[key] ? validating[key] : true;

                    return (
                      <div
                        className={`hayley-flex hayley-h-[56px] hayley-w-full hayley-flex-1 hayley-flex-col hayley-justify-center hayley-rounded-[2px] hayley-border hayley-border-solid ${
                          isValid ? "hayley-border-gray-300" : "hayley-border-red-600"
                        } hayley-bg-white hayley-px-[10px] hayley-py-[6px] hayley-font-opensans hayley-text-sm hayley-leading-6 hayley-text-secondary placeholder:hayley-text-gray-400`}
                      >
                        {inputValues[key] && (
                          <label className="hayley-mb-2 hayley-h-3 hayley-text-gray-600">{`${t(placeholder)} ${isRequired ? "*" : ""}`}</label>
                        )}
                        <Input
                          key={key || uuidv4()}
                          className={`hayley-border-none focus:hayley-outline-none${
                            idx % 2 === 0 && idx === replies.options.length - 1 ? " hayley-col-span-2" : ""
                          }`}
                          type={type || "text"}
                          placeholder={`${t(placeholder)} ${isRequired ? "*" : ""}`}
                          disabled={optionDisabled}
                          value={inputValues[key] || ""}
                          onChange={(e) => onChange(e, key, validate)}
                          onKeyDown={onKeyPress}
                          inputComponent={validate === "PHONE" ? PhoneMaskCustom : undefined}
                          disableUnderline
                          ref={idx === 0 ? firstInputRef : null}
                          inputProps={{ "data-testid": key }}
                        />
                      </div>
                    );
                  }
                )}
              </div>

              {replies.options.length <= 2 ? (
                <CustomTooltip title={errorMessage} placement="left">
                  <span className="hayley-mb-1">
                    <button
                      className={`hayley-flex hayley-h-11 hayley-w-11 hayley-cursor-pointer hayley-items-center hayley-justify-center hayley-rounded-full hayley-text-white hayley-border-none${
                        !disabled ? "" : " hayley-opacity-10"
                      }`}
                      style={{ backgroundColor }}
                      onClick={onClickSend}
                      disabled={optionDisabled || disabled}
                      data-testid="contactUsSend"
                    >
                      <Icon iconType="Send" className="-hayley-ml-1 hayley-h-[34px] hayley-w-[34px] hayley-text-inherit" />
                    </button>
                  </span>
                </CustomTooltip>
              ) : null}
            </div>

            {(isScheduleTourForm && showTourTransactionMsg) || (!isScheduleTourForm && showContactTransactionMsg) ? (
              <div
                data-testid={isScheduleTourForm ? "scheduleTourForm_requestUpdate" : "contactUsForm_requestUpdate"}
                className="hayley-flex hayley-items-center hayley-space-x-2 hayley-pt-3 hayley-text-secondary"
              >
                <Checkbox
                  data-testid="scheduleTourForm_requestUpdate_checkbox"
                  className="hayley-p-0"
                  checked={isScheduleTourForm ? isCheckedTourTransaction : isCheckedContactTransaction}
                  onChange={() => handleTourTransactionChange(isScheduleTourForm)}
                  disabled={isDisabledCheckbox}
                  sx={{
                    // color: pink[800],
                    "&.Mui-checked": {
                      color: backgroundColor,
                    },
                  }}
                />
                <span
                  className={`${!isDisabledCheckbox && "hayley-cursor-pointer"} ${isDisabledCheckbox ? "hayley-text-custom-4" : ""}`}
                  onClick={() => (isDisabledCheckbox ? null : setIsChecked(!isChecked))}
                >
                  {isScheduleTourForm ? tourTransactionMsg : contactTransactionMsg}
                </span>
              </div>
            ) : null}

            {(isScheduleTourForm && showTourPromotionMsg) || (!isScheduleTourForm && showContactPromotionMsg) ? (
              <div
                data-testid={isScheduleTourForm ? "scheduleTourForm_sendPromotion" : "contactUsForm_sendPromotion"}
                className="hayley-flex hayley-items-center hayley-space-x-2 hayley-pt-3 hayley-text-secondary"
              >
                <Checkbox
                  data-testid="scheduleTourForm_sendPromotion_checkbox"
                  className="hayley-p-0"
                  checked={isScheduleTourForm ? isCheckedTourPromotion : isCheckedContactPromotion}
                  onChange={() => handleTourPromotionChange(isScheduleTourForm)}
                  disabled={isDisabledCheckbox}
                  sx={{
                    // color: pink[800],
                    "&.Mui-checked": {
                      color: backgroundColor,
                    },
                  }}
                />
                <span
                  className={`${!isDisabledCheckbox && "hayley-cursor-pointer"} ${isDisabledCheckbox ? "hayley-text-custom-4" : ""}`}
                  onClick={() => (isDisabledCheckbox ? null : setIsChecked(!isChecked))}
                >
                  {isScheduleTourForm ? tourPromotionMsg : contactPromotionMsg}
                </span>
              </div>
            ) : null}
          </>
        )}
      </div>

      {scheduleDateAvail || (self_tour_link && tourType == "self_guided_tour") || (isScheduleTourForm && !availableDateSlots.length) ? null : (
        <>
          {replies.options.length > 2 && !replies.bareMinimum ? (
            <div className="hayley-w-full">
              <CustomFormQuestion
                setQuestionDisabled={setQuestionDisabled}
                optionDisabled={optionDisabled}
                customQuestions={customQuestions}
                setCustomQuestions={setCustomQuestions}
              />
            </div>
          ) : null}

          <div className="hayley-w-full">
            {getDisclaimer(disclaimerData?.orgLevelDisclaimer).message.length > 1 && isFormDisclaimerEnabled && (
              <div className="hayley-my-2 hayley-max-w-[725px] hayley-text-sm hayley-text-gray-600" data-testid="disclaimer">
                <div dangerouslySetInnerHTML={{ __html: getDisclaimer(disclaimerData?.orgLevelDisclaimer).message }} />
              </div>
            )}
          </div>

          <div
            className={clsx(
              `${!isScheduleTourForm ? "-hayley-mx-4 hayley-bg-white hayley-px-4" : "-hayley-mx-3 hayley-bg-[#FBFBFB] hayley-px-3 "}`, // Adjust "another-class" as needed
              "hayley-sticky -hayley-bottom-4  hayley-my-[1px] hayley-flex hayley-w-[inherit] hayley-gap-3 hayley-overflow-hidden   hayley-py-3",
              { "hayley-shadow-onesided": showShadow }
            )}
          >
            <CustomTooltip title={errorMessage} placement="left">
              <ButtonComp
                label={isScheduleTourForm ? (isRescheduleForm ? t("rescheduleTour") : t("scheduleTour")) : t("submit")}
                onClick={onClickSend}
                disabled={optionDisabled || disabled || (isScheduleTourForm ? questionDisabled : false)}
                filled={true}
                testId={isScheduleTourForm ? "scheduleTourForm_scheduleButton" : "contactUsForm_submitButton"}
              />
            </CustomTooltip>
            <ButtonComp testId="cancelButton" label={t("cancel")} onClick={onCancel} />
          </div>
        </>
      )}
    </div>
  );
};

export default MultiInputView;
